import React, { useEffect, useState } from "react";
import API from "../services/api";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Detailed Modal State
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchingDetail, setFetchingDetail] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await API.get("/users");
      if (res.data.success) {
        setUsers(res.data.users);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (userId) => {
    try {
      setFetchingDetail(true);
      const res = await API.get(`/user/${userId}`); // Calls getSingleUser
      if (res.data.success) {
        setSelectedUser(res.data.user);
        setIsModalOpen(true);
      }
    } catch (err) {
      alert("Error fetching user details");
    } finally {
      setFetchingDetail(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return <div style={styles.loading}>Loading community directory...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>User Management</h2>
          <p style={styles.subtitle}>View and manage registered passengers</p>
        </div>
        <div style={styles.badgeCount}>
          {users.length} Total Users
        </div>
      </div>

      {/* GRID */}
      {users.length === 0 ? (
        <div style={styles.empty}>No users found in the database.</div>
      ) : (
        <div style={styles.grid}>
          {users.map((user) => (
            <div key={user._id} style={styles.userCard}>
              <div style={styles.cardMain}>
                <div style={styles.userInfo}>
                  <div style={styles.avatarPlaceholder}>
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={styles.userName}>{user.name}</div>
                    <div style={styles.userEmail}>{user.email}</div>
                  </div>
                </div>

                <div style={styles.divider} />
                
                <div style={styles.detailRow}>
                  <span style={styles.label}>Phone</span>
                  <span style={styles.value}>{user.phone || "N/A"}</span>
                </div>

                <button 
                  style={styles.viewBtn} 
                  onClick={() => handleViewUser(user._id)}
                  disabled={fetchingDetail}
                >
                  {fetchingDetail ? "..." : "View Full Profile"}
                </button>
              </div>

              <div style={styles.cardFooter}>
                ID: {user._id.slice(-6).toUpperCase()} • Joined {new Date(user.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* USER DETAIL MODAL */}
      {isModalOpen && selectedUser && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>Detailed Profile</h3>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailItem}>
                <strong>Full Name:</strong> <span>{selectedUser.name}</span>
              </div>
              <div style={styles.detailItem}>
                <strong>Email Address:</strong> <span>{selectedUser.email}</span>
              </div>
              <div style={styles.detailItem}>
                <strong>Phone Number:</strong> <span>{selectedUser.phone}</span>
              </div>
              <div style={styles.detailItem}>
                <strong>Terms Status:</strong> 
                <span style={selectedUser.acceptTerms ? styles.statusGreen : styles.statusRed}>
                   {selectedUser.acceptTerms ? "Accepted" : "Not Accepted"}
                </span>
              </div>
              <div style={styles.detailItem}>
                <strong>Push Notifications:</strong> 
                <span>{selectedUser.expoPushToken ? "Token Active" : "Not Setup"}</span>
              </div>
              <div style={styles.detailItem}>
                <strong>Database ID:</strong> <code style={{fontSize: '11px'}}>{selectedUser._id}</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= STYLES ================= */
// (Extending your existing styles)

const styles = {
  // ... Paste your existing styles here ...
  page: { padding: "20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" },
  title: { margin: 0, fontSize: "26px", fontWeight: "700", color: "#0f172a" },
  subtitle: { margin: "5px 0 0 0", fontSize: "14px", color: "#64748b" },
  badgeCount: { background: "#6366f1", color: "white", padding: "10px 18px", borderRadius: "12px", fontSize: "14px", fontWeight: "600" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "20px" },
  userCard: { background: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0", display: "flex", flexDirection: "column" },
  cardMain: { padding: "20px" },
  userInfo: { display: "flex", alignItems: "center", gap: "15px", marginBottom: "20px" },
  avatarPlaceholder: { width: "48px", height: "48px", background: "#e0e7ff", color: "#4338ca", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: "bold" },
  userName: { fontSize: "16px", fontWeight: "700", color: "#1e293b" },
  userEmail: { fontSize: "13px", color: "#64748b" },
  divider: { height: "1px", background: "#f1f5f9", margin: "0 0 15px 0" },
  detailRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
  label: { fontSize: "12px", fontWeight: "500", color: "#94a3b8", textTransform: "uppercase" },
  value: { fontSize: "13px", fontWeight: "600", color: "#334155" },
  viewBtn: { width: "100%", padding: "10px", background: "#f1f5f9", border: "none", borderRadius: "8px", color: "#475569", fontWeight: "600", cursor: "pointer", transition: "0.2s" },
  cardFooter: { background: "#f8fafc", padding: "12px 20px", fontSize: "11px", color: "#94a3b8", borderTop: "1px solid #e2e8f0", borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" },
  statusGreen: { color: "#059669", background: "#ecfdf5", padding: "2px 8px", borderRadius: "6px", fontSize: "12px" },
  statusRed: { color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: "6px", fontSize: "12px" },
  
  // Modal Styles
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "white", padding: "25px", borderRadius: "20px", width: "90%", maxWidth: "450px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "15px", marginBottom: "15px" },
  closeBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8" },
  modalBody: { display: "flex", flexDirection: "column", gap: "15px" },
  detailItem: { display: "flex", justifyContent: "space-between", fontSize: "14px", borderBottom: "1px dashed #f1f5f9", paddingBottom: "8px" },
  loading: { textAlign: "center", padding: "50px", color: "#6366f1" },
  error: { color: "#dc2626", padding: "20px", textAlign: "center" },
  empty: { textAlign: "center", padding: "100px", color: "#94a3b8" }
};

export default Users;